import type {
  Attachment,
  GeneratedFile,
  ProjectMessage,
  ProjectPlan,
  StripeConnection,
  SupabaseProject,
  TableDef,
} from "./types";
import {
  fromBuildAttemptRow,
  type BuildAttempt,
  type BuildAttemptRow,
  type UpsertBuildAttemptInput,
} from "./build-attempt";
import {
  PLATFORM_BODY_LIMIT_BYTES,
  chunkFilesBySize,
  oversizeFiles,
  serializedFileBytes,
  serializedFilesBodyBytes,
} from "./payload/file-chunks";
import type { PublishedVersionResponse } from "./projects/published-version";
import { shouldRetrySave } from "./db/save-failure-code";
import { recoverSession } from "./auth/session-recovery";
import type {
  StripeDisconnectIntent,
  StripeReleaseOutcome,
} from "./stripe-disconnect";

export class ApiError extends Error {
  code?: string;
  status: number;
  /** The full parsed error body, when present — lets callers read structured
   *  fields (e.g. a 422's `typeErrors[]`) without a second parse. */
  details?: unknown;
  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * True only for connection-level fetch failures — the request never received a
 * response (dropped wifi, DNS blip, captive portal). `fetch()` rejects with a
 * TypeError in this case; the wording differs per browser (Chrome "Failed to
 * fetch", Firefox "NetworkError when attempting to fetch resource", Safari
 * "Load failed"). HTTP error *statuses* resolve normally and surface as
 * ApiError, so this never reports a deterministic 4xx/5xx as transient.
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof ApiError) return false;
  if (!(err instanceof TypeError)) return false;
  const m = err.message.toLowerCase();
  return (
    m.includes("fetch") ||
    m.includes("network") ||
    m.includes("load failed") ||
    m.includes("connection")
  );
}

/** How a build/preview failure should be explained to the user (PANDA-1101). */
export type BuildErrorKind = "offline" | "server" | "unknown";

/**
 * Classify a build/preview failure for user-facing messaging.
 *
 * A connection-level `fetch` failure (see isNetworkError) is NOT proof that the
 * user's internet dropped — the very same "Failed to fetch" TypeError is raised
 * when the request dies at the server/gateway (a timed-out serverless function,
 * a 502/503/504 at the edge, a reset stream) while the client stays perfectly
 * online. Blaming the user's connection in that case is the false "your
 * connection dropped" warning reported in PANDA-1101.
 *
 * `navigator.onLine === false` is the only reliable signal that the client
 * itself went offline — the same authority handleChatSend already gates sends
 * on. So:
 *   - "offline": fetch failure AND the browser reports it is offline.
 *   - "server": fetch failure while the browser reports it is online → the
 *     failure is server/gateway-side; show a server error, not a network one.
 *   - "unknown": anything else (ApiError, app exceptions) → generic copy.
 */
export function classifyBuildError(err: unknown): BuildErrorKind {
  if (!isNetworkError(err)) return "unknown";
  // SSR / non-browser: no navigator to consult — don't blame the user's network.
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  return offline ? "offline" : "server";
}

export const NETWORK_RETRY_BASE_MS = 400;

/** Why waitForConnectivity stopped waiting. */
export type ConnectivityWait =
  /** Already usable, or the browser came back before the deadline. */
  | "ready"
  /** Still offline / still hidden when the deadline passed — retry anyway. */
  | "timeout";

/**
 * Resolve once the browser is plausibly able to complete a request again.
 *
 * Retrying a connection-level failure the instant it happens is worthless: the
 * network that just refused the request is still refusing it microseconds
 * later, so the retry budget is spent on two identical failures in the same
 * tick (the build-recovery loop this exists to fix). Two conditions make a
 * retry pointless, and both announce their own recovery via an event:
 *   • navigator.onLine === false — no route out; the `online` event fires when
 *     there is one again.
 *   • document.visibilityState === "hidden" — a backgrounded tab is throttled
 *     and, on mobile, frozen outright: its fetch dies on the spot. The
 *     `visibilitychange` back to visible is when a retry can actually run.
 *
 * Neither signal is authoritative about the SERVER (see classifyBuildError),
 * which is why this only ever DELAYS a retry the caller had already decided to
 * make — it never decides whether to retry. On timeout we return "timeout" and
 * let the caller proceed regardless: waiting forever on a wrong `onLine` reading
 * would be worse than one wasted attempt.
 */
export function waitForConnectivity(
  timeoutMs: number,
): Promise<ConnectivityWait> {
  // SSR / non-browser: nothing to consult and no events to wait on.
  if (typeof navigator === "undefined" || typeof document === "undefined") {
    return Promise.resolve("ready");
  }
  const usable = () =>
    navigator.onLine !== false && document.visibilityState !== "hidden";
  if (usable()) return Promise.resolve("ready");

  return new Promise<ConnectivityWait>((resolve) => {
    const settle = (result: ConnectivityWait) => {
      clearTimeout(timer);
      window.removeEventListener("online", onMaybeReady);
      document.removeEventListener("visibilitychange", onMaybeReady);
      resolve(result);
    };
    // Either event can fire while the OTHER condition is still blocking (coming
    // back to a visible tab that is still offline), so re-check both rather than
    // treating the event itself as proof.
    function onMaybeReady() {
      if (usable()) settle("ready");
    }
    // Declared after `settle` on purpose — the two reference each other, and
    // both are only ever dereferenced from a callback, long after this runs.
    const timer = setTimeout(() => settle("timeout"), timeoutMs);
    window.addEventListener("online", onMaybeReady);
    document.addEventListener("visibilitychange", onMaybeReady);
  });
}

/**
 * fetch() that retries ONLY on a connection-level failure (see isNetworkError),
 * with exponential backoff. It never inspects res.ok — HTTP error statuses are
 * returned to the caller untouched, so a retry can never mask a real 4xx/5xx.
 * Because fetch only rejects when no response was received, retrying is safe
 * even for non-idempotent writes in the overwhelmingly common case (the request
 * never reached the server).
 */
export async function fetchWithNetworkRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  retries = 2,
): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch(input, init);
    } catch (err) {
      if (isNetworkError(err) && attempt < retries) {
        await new Promise((r) =>
          setTimeout(r, NETWORK_RETRY_BASE_MS * 2 ** attempt),
        );
        continue;
      }
      throw err;
    }
  }
}

/**
 * fetch() for a route that authenticates from the session cookie.
 *
 * ── Why this is not just inside apiFetch ────────────────────────────────────
 * The recovery first landed in `apiFetch`, which covers every JSON call — and
 * missed the ones that matter most. The build and edit routes stream their
 * response, so they are called with a bare `fetch` and never touched it. Tested
 * against an expired session, the save path reported "Your session expired —
 * sign in again", exactly as intended, while the EDIT that produced the change
 * had already failed one layer earlier as a bare `Server error (HTTP 401)` and
 * come back to the user as "Something went wrong on my end while applying that".
 *
 * Two answers to one fault, and the honest one was unreachable because the turn
 * died before it. So the rule lives here, in front of both: any authenticated
 * request gets one refresh and one re-send, whether its reply is JSON or a
 * stream.
 *
 * Returns the Response untouched — a streaming caller needs the body, and
 * status handling stays theirs. `apiFetch` layers the typed error on top.
 */
export async function authedFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetchWithNetworkRetry(path, init);
  // EXACTLY ONE retry. A second 401 after a fresh session is a real
  // authorization answer, not a stale one, and looping on it would turn a clear
  // failure into a hang. The re-send is safe: `body` is a string at every call
  // site, and the first request was rejected before the route ran.
  if (res.status !== 401) return res;
  return (await recoverSession()) ? fetchWithNetworkRetry(path, init) : res;
}

/**
 * A 401 IS A STALE CREDENTIAL, NOT A FAILED OPERATION.
 *
 * Every route here authenticates from the session cookie. When the refresh
 * token is rejected — `[auth] middleware session refresh failed` in the server
 * log — every request answers 401 until something re-runs the auth flow, and
 * until now the only thing that did was a full page load. That is the whole of
 * the reported "saving is stuck, Retry does nothing, a hard refresh fixes it
 * and loses my work": the save was never the broken part.
 *
 * So the recovery lives at the transport, once, where every caller gets it:
 * refresh, send the same request again, and say nothing if it works. Exactly
 * ONE retry — a second 401 after a fresh session is a real authorization
 * answer, not a stale one, and looping on it would turn a clear failure into a
 * hang.
 *
 * The re-send is safe for writes: `body` is always a string here, and the first
 * request was rejected before the route ran, so nothing was applied twice.
 */
async function apiFetch(path: string, options?: RequestInit) {
  const init: RequestInit = {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  };
  const res = await authedFetch(path, init);

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // NAMED, so the UI can stop offering a control that cannot work. A save
    // blocked on authentication has no Retry that succeeds — it has a sign-in —
    // and every layer above needs to be able to tell the two apart without
    // reading prose.
    if (res.status === 401) {
      throw new ApiError(
        "Your session expired — sign in again to continue.",
        401,
        "session_expired",
        body,
      );
    }
    throw new ApiError(
      body.error || `API error ${res.status}`,
      res.status,
      body.code,
      body,
    );
  }
  return res.json();
}

export async function createProject(prompt: string, id?: string, templateId?: string) {
  return apiFetch("/api/projects", {
    method: "POST",
    body: JSON.stringify({ prompt, id, templateId }),
  });
}

export async function getProject(id: string): Promise<{
  project: {
    id: string;
    title: string;
    prompt: string;
    plan: ProjectPlan | null;
supabase_url: string | null;
    supabase_anon_key: string | null;
    supabase_project_ref: string | null;
    db_enabled: boolean;
    github_repo: string | null;
    vercel_project_id: string | null;
    vercel_url: string | null;
    deploy_status: string | null;
    last_deployed_at?: string | null;
    updated_at?: string | null;
    created_at?: string | null;
    // Which of the owner's projects this is, 1-based and counted server-side.
    // Null when the count could not be read — it is telemetry (the
    // post-first-build $1 offer reports it), never load-bearing.
    projectOrdinal?: number | null;
    // PANDA-1281: set once the first-build value-realization line has been
    // shown; absent (or null) means it hasn't, so the client may show it on the
    // next successful build. Optional — absent until migration 138 runs.
    first_build_value_shown_at?: string | null;
    // The once-per-entity placeholder-data handoff record (see
    // lib/build/placeholder-handoff). Absent until migration 144 runs; validate
    // with sanitizePlaceholderHandoffState before reading.
    placeholder_handoff?: unknown;
    screenshot_url?: string | null;
    // Publish metadata (PANDA-1111) — optional; absent until migration 133 runs.
    meta_title?: string | null;
    meta_description?: string | null;
    og_image_url?: string | null;
    favicon_url?: string | null;
  };
  messages: {
    id: string;
    role: string;
    content: string;
    created_at: string;
    attachments?: Attachment[];
    feedback_rating?: 1 | -1 | null;
    feedback_reason?: string | null;
    feedback_comment?: string | null;
  }[];
  files: GeneratedFile[];
  hasOAuthToken: boolean;
  stripeConnection: StripeConnection | null;
}> {
  return apiFetch(`/api/projects/${id}`);
}

export async function updateProject(
  id: string,
  data: {
    title?: string;
    plan?: ProjectPlan;
    db_enabled?: boolean;
    screenshot_url?: string;
    badge_removed?: boolean;
    is_public?: boolean;
    posthog_enabled?: boolean;
    posthog_api_key?: string | null;
    posthog_api_host?: string | null;
    posthog_ui_host?: string | null;
    // Publish metadata (PANDA-1111). Saved best-effort by the PATCH route.
    meta_title?: string | null;
    meta_description?: string | null;
    og_image_url?: string | null;
    favicon_url?: string | null;
  },
) {
  return apiFetch(`/api/projects/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// Per-request budget for the file save. A large project can't ship every file
// in one POST — the platform request-size limit (~4.5MB on Vercel) rejects it
// with a 413 before the route runs, and the save is silently lost. Kept under
// that ceiling with headroom for the JSON envelope; larger sets are split into
// several requests (each file is an independent versioned row server-side, so
// batching is lossless).
// Deliberately BELOW the shared transport budget (SAFE_BODY_BYTES). The
// platform ceiling is only one of two limits here: this route also writes a
// versioned row per file, so a bigger batch is a longer-running request. The
// extra headroom buys route duration, not transport safety.
// PANDA-1917: the sizing itself is no longer this file's problem — it delegates
// to the one exact measurement in lib/payload/file-chunks.ts.
const SAVE_CHUNK_BYTES = 3_000_000;

/**
 * What a save returns once migration 162 is applied. `snapshotSkipped` is the
 * degrade signal: the files WERE saved, but no point-in-time snapshot was
 * recorded (the table isn't there yet). Callers must treat it as "history is
 * unavailable", never as a failed save.
 */
export interface SaveFilesResult {
  saved?: number;
  droppedPaths?: string[];
  snapshotId?: string | null;
  snapshotSeq?: number | null;
  snapshotSkipped?: boolean;
  /** Tombstones actually written. Absent when the request asked for none. */
  deleted?: number;
  /**
   * Paths the server would NOT delete — the boot-critical floor (app/layout.tsx
   * and friends), or a path this same save also wrote. The caller must treat
   * these as still present rather than assuming the deletion landed, or it will
   * report a clean reversal the user can see is false.
   */
  refusedDeletions?: string[];
}

/**
 * Which turn this save belongs to, so the server can group a turn's several
 * saves into ONE snapshot.
 *
 * A single user prompt writes 1-10 times (the edit itself, up to five autofix
 * rounds, escalation rollbacks, per-phase saves). Without `turnId` each of those
 * would mint its own history entry and "undo one turn" would land on that turn's
 * fourth autofix. The server keys on it via a partial unique index, so repeated
 * saves in a turn rewrite the same row instead of appending.
 */
export interface SaveFilesMeta {
  turnId?: string;
  /** The user's own words for this turn — version history's label. */
  label?: string;
  source?: "save" | "restore" | "clone";
  /**
   * Paths to remove. The server writes a TOMBSTONE per path (a row marked
   * deleted at the next version), never a hard DELETE — so the content is still
   * on disk and stepping forward again re-inserts the exact bytes. That is what
   * makes redo safe after an undo that removed files.
   *
   * Refused paths come back as `refusedDeletions`: the app cannot boot without
   * app/layout.tsx and friends, so the floor lives server-side and the caller is
   * told rather than silently ignored.
   */
  deletedPaths?: string[];
  /**
   * For `source: "restore"` only — the seq whose bytes this restore
   * materialised. The undo/redo cursor is DERIVED from it, so omitting it makes
   * a restore indistinguishable by position from new work and the next undo
   * walks the wrong way (it re-applies the change just removed). Sent only on
   * restores, because a pre-163 database rejects the extra argument outright and
   * that would cost the turn its history entry.
   */
  restoredFromSeq?: number;
}

/**
 * One save attempt that failed and is about to be repeated.
 *
 * Handed to the caller so the UI can say "still syncing" while it happens.
 * Without this the workspace had no way to tell a save that is being retried
 * from one that has given up — the reported defect, in which a transient 500
 * was announced as `Having trouble saving. Your changes will sync shortly.`
 * and then never mentioned again in either direction.
 */
export interface SaveRetryNotice {
  /** The attempt that just failed, 1-based. */
  attempt: number;
  /** How many attempts will be made in total. */
  attempts: number;
  /** The route's structured failure code, when it sent one. */
  code: string | null;
  status: number | null;
  /** How long we are about to wait before trying again. */
  delayMs: number;
}

export interface SaveFilesOptions {
  /** Called before each retry. Never called when the first attempt lands. */
  onRetry?: (notice: SaveRetryNotice) => void;
}

/**
 * How many times ONE save request is sent before the failure is the user's
 * problem. Three, not more: the server settles version races by itself now
 * (app/api/projects/[id]/files/route.ts), so what reaches here is a genuinely
 * unavailable database or a gateway fault, and a fourth attempt mostly buys the
 * user another two seconds of a spinner that is going to end the same way.
 */
export const SAVE_ATTEMPTS = 3;
/** 500ms, then 1500ms. Long enough to outlast a redeploy blip, short enough
 *  that a real failure is reported while the user is still looking. */
const SAVE_RETRY_DELAYS_MS = [500, 1500];

/**
 * POST one save batch, retrying the faults that are worth retrying.
 *
 * `apiFetch` already retries CONNECTION-level failures (fetchWithNetworkRetry),
 * and deliberately never inspects res.ok — an HTTP status is the route's
 * answer, not a transport fault, so retrying it blindly could repeat a
 * non-idempotent write for no reason. This layer is the other half: it retries
 * exactly the statuses the route has DECLARED retryable, and nothing else.
 *
 * Safe to repeat: a save is append-only (`version = max + 1` per path) and
 * every save writes the whole project, so a duplicated request is at worst one
 * redundant version, never a lost or interleaved edit.
 */
async function postSaveBatch(
  url: string,
  body: unknown,
  options?: SaveFilesOptions,
): Promise<SaveFilesResult> {
  for (let attempt = 1; ; attempt++) {
    try {
      return (await apiFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
      })) as SaveFilesResult;
    } catch (err) {
      const retryable =
        err instanceof ApiError &&
        shouldRetrySave({
          code: err.code,
          status: err.status,
          retryable: (err.details as { retryable?: boolean } | undefined)
            ?.retryable,
        });
      if (!retryable || attempt >= SAVE_ATTEMPTS) throw err;
      const delayMs =
        SAVE_RETRY_DELAYS_MS[attempt - 1] ??
        SAVE_RETRY_DELAYS_MS[SAVE_RETRY_DELAYS_MS.length - 1];
      const apiErr = err as ApiError;
      console.warn(
        `[saveFiles] attempt ${attempt}/${SAVE_ATTEMPTS} failed ` +
          `(status=${apiErr.status} code=${apiErr.code ?? "none"}) — ` +
          `retrying in ${delayMs}ms`,
      );
      options?.onRetry?.({
        attempt,
        attempts: SAVE_ATTEMPTS,
        code: apiErr.code ?? null,
        status: apiErr.status ?? null,
        delayMs,
      });
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
}

export async function saveFiles(
  projectId: string,
  files: GeneratedFile[],
  meta?: SaveFilesMeta,
  options?: SaveFilesOptions,
): Promise<SaveFilesResult> {
  const url = `/api/projects/${projectId}/files`;
  // Mint a turn id when the caller supplies none. The server groups a save's
  // several requests by this key, so WITHOUT one a chunked save (anything over
  // SAVE_CHUNK_BYTES) mints a separate history entry per chunk — each a full
  // copy of the project, each dragging the head through a partial state. Callers
  // that don't know about turns (the protocol-leak heal, the Slack/Telegram
  // webhook edit) still deserve exactly one entry for one save.
  // Deletions ride exactly ONE request, never every batch. They are pulled out
  // of `extra` here because the server refuses a path that the SAME request also
  // writes (they would collide on UNIQUE(project_id, file_path, version)) — so
  // spreading them across batches would let a path be tombstoned in batch 1 and
  // rewritten in batch 3, resurrecting it with the collision guard never firing,
  // because the write and the delete never appear in one request. Sent on the
  // LAST batch, after every write has landed.
  const { deletedPaths, ...meta0 } = meta ?? {};
  const extra: SaveFilesMeta = {
    ...meta0,
    turnId: meta?.turnId ?? crypto.randomUUID(),
  };
  const deletions =
    deletedPaths && deletedPaths.length > 0 ? { deletedPaths } : {};

  // A file bigger than a whole request cannot be chunked around — chunkFilesBySize
  // emits it alone and it is rejected at the EDGE, before the route runs, as a
  // bare `API error 413` with no indication of which file.
  //
  // The preview path has detected this before sending since PANDA-1917
  // (filesTooLargeToSend); the save path never did, so the same oversized module
  // produced a named, actionable failure on one transport and an anonymous one
  // on the other. Worse, the earlier batches have already COMMITTED by then, so
  // the project is left holding this turn's other edits beside a stale copy of
  // the file that failed — a partial save nothing announces as partial.
  //
  // Measured against the PLATFORM ceiling, not SAVE_CHUNK_BYTES: the smaller
  // budget exists to bound request DURATION (a batch writes one row per file),
  // and a lone file over it still saves perfectly well. Only the edge limit is a
  // real wall.
  const unsendable = oversizeFiles(files, PLATFORM_BODY_LIMIT_BYTES);
  if (unsendable.length > 0) {
    const named = unsendable
      .map((f) => `${f.path} (${Math.round(serializedFileBytes(f) / 1024)}KB)`)
      .join(", ");
    console.error(
      `[saveFiles] REFUSING to send — ${unsendable.length} file(s) exceed the ` +
        `${PLATFORM_BODY_LIMIT_BYTES}B platform limit: ${named}`,
    );
    // 413 so existing callers keep their transport-too-large handling; `details`
    // carries the paths so the toast can name the file instead of asking the
    // user to guess which one grew.
    throw new ApiError(
      `A file is too large to save: ${named}`,
      413,
      "files_too_large",
      { oversizePaths: unsendable.map((f) => f.path) },
    );
  }

  // Common case: the whole set fits — one request, unchanged behaviour.
  // Exact serialized bytes, not `JSON.stringify(...).length` — that counts
  // UTF-16 code units, so an app full of emoji or CJK measured well under the
  // budget and still 413'd at the edge (PANDA-1917).
  if (serializedFilesBodyBytes(files) <= SAVE_CHUNK_BYTES) {
    return await postSaveBatch(url, { files, ...extra, ...deletions }, options);
  }

  // Large project: split into size-bounded batches so no single request 413s.
  // Sequential so version numbering is computed against a settled DB state.
  //
  // Every batch carries the SAME turnId on purpose. The server derives each
  // snapshot from the `files` table rather than from the request body, so a
  // partial batch produces a progressively-completing snapshot and the last one
  // completes it — one history entry, not one per chunk. Building the snapshot
  // from the body instead would make the final chunk look like the whole
  // project and collapse history to it.
  const batches = chunkFilesBySize(files, SAVE_CHUNK_BYTES);
  console.warn(
    `[saveFiles] payload over ${SAVE_CHUNK_BYTES}B — saving ${files.length} file(s) in ${batches.length} batch(es)`,
  );
  let last: SaveFilesResult = {};
  for (let i = 0; i < batches.length; i++) {
    const isLast = i === batches.length - 1;
    last = await postSaveBatch(
      url,
      { files: batches[i], ...extra, ...(isLast ? deletions : {}) },
      options,
    );
  }
  return last;
}

/** One entry in version history — metadata only, never the file blob. */
export interface ProjectSnapshotMeta {
  id: string;
  seq: number;
  turnId: string | null;
  /** The user's own words for the turn ("add a gallery page"), when known. */
  label: string | null;
  source: "save" | "restore" | "clone" | "backfill";
  fileCount: number;
  createdAt: string;
}

/**
 * Where undo and redo stand right now (migration 163 §2).
 *
 * DERIVED server-side over the WHOLE ledger, not over the page `snapshots`
 * happens to carry: every undo appends a restore row, so a long undo/redo
 * session walks the anchor straight out of a 100-row window and a page-local
 * cursor would start refusing on history that is plainly still there.
 *
 * `cursorSeq` is the one discriminator. Null means this database cannot derive a
 * cursor at all (migration 163 not applied yet, or the read failed), and the
 * other three are null with it — a caller must fall back to what it did before
 * the cursor existed rather than read `undoSeq: null` as "nothing to undo to".
 * With `cursorSeq` non-null, `undoSeq`/`redoSeq` null DO mean that step is
 * disabled: there is no earlier / later real state to go to.
 */
export interface SnapshotCursor {
  /** The stored state the project is at — its own seq, never a step target. */
  cursorSeq: number | null;
  undoSeq: number | null;
  redoSeq: number | null;
  /** Newest seq in the ledger. Equal to `cursorSeq` until an undo moves it. */
  tipSeq: number | null;
}

/**
 * Version history, as real points in time.
 *
 * `available: false` means migration 162 has not been applied — NOT that the
 * project has no history. The two must stay distinguishable: the old
 * `/versions` routes fail open to an empty list, so an auth blip reads as "you
 * have no history", which is exactly the kind of confident-but-wrong answer this
 * whole change exists to remove.
 */
export async function getSnapshots(
  projectId: string,
  opts?: { limit?: number; before?: number },
): Promise<
  {
    available: boolean;
    snapshots: ProjectSnapshotMeta[];
    headSnapshotId: string | null;
  } & SnapshotCursor
> {
  const qs = new URLSearchParams();
  if (opts?.limit) qs.set("limit", String(opts.limit));
  if (opts?.before) qs.set("before", String(opts.before));
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch(`/api/projects/${projectId}/snapshots${suffix}`);
}

/**
 * One snapshot's complete file set — the exact bytes to restore.
 *
 * Throws rather than returning an empty set when the snapshot cannot be read.
 * A caller that mistook "unavailable" for "empty" would restore nothing over a
 * whole project.
 */
export async function getSnapshot(
  projectId: string,
  seq: number,
): Promise<{
  seq: number;
  snapshotId: string;
  label: string | null;
  createdAt: string;
  source: string;
  files: GeneratedFile[];
}> {
  return apiFetch(`/api/projects/${projectId}/snapshots/${seq}`);
}

/**
 * Persist one chat message.
 *
 * `presentation` (migration 146) is what makes a build read as a build after a
 * reload — its card, and a quick edit's short teaser. It rides on the row itself
 * so it comes back with the row on ANY device, rather than only in the browser
 * that produced it (the pre-146 localStorage snapshot). Optional end to end: a
 * database without the columns drops it server-side and says so.
 */
export async function saveMessage(
  projectId: string,
  role: ProjectMessage["role"],
  content: string,
  attachments?: Attachment[],
  presentation?: {
    kind?: ProjectMessage["kind"];
    recapShort?: string;
    recapInCard?: boolean;
    buildCard?: {
      title?: string;
      summary?: string;
      detail?: string;
      steps?: string[];
      stepsDone?: number;
      failed?: boolean;
    };
    /**
     * The remaining recap slabs (migration 171). `note` is the always-visible
     * admission appended to the end of `content` — sent as the STORED variant,
     * which is what matches the stored content. `changes` is the affected-areas
     * breakdown shown inside the Details expansion.
     */
    recapParts?: { note?: string; changes?: string };
    /** The row's real timeline position — sent only for a build card, which is
     *  written after its build settles and would otherwise sort to the end. */
    createdAt?: string;
  },
) {
  return apiFetch(`/api/projects/${projectId}/messages`, {
    method: "POST",
    body: JSON.stringify({ role, content, attachments, ...presentation }),
  });
}

/**
 * Create-or-update the durable first-build attempt record (migration 141).
 * A full payload is sent once at creation (before planning); later calls send
 * only { buildAttemptId, stage } / { status } as a cheap heartbeat/transition.
 * Callers treat this as best-effort — a persistence hiccup must never break the
 * live build.
 */
export async function upsertBuildAttempt(
  projectId: string,
  input: UpsertBuildAttemptInput,
): Promise<BuildAttempt | null> {
  const data = (await apiFetch(`/api/projects/${projectId}/build-attempt`, {
    method: "POST",
    body: JSON.stringify(input),
  })) as { attempt: BuildAttemptRow | null };
  return data.attempt ? fromBuildAttemptRow(data.attempt) : null;
}

/**
 * Claim the project's prepared build for resume — the server-side single-flight
 * boundary (migration 186).
 *
 * Returns the attempt ONLY when this caller is the one that moved it out of
 * `awaiting_entitlement`. Everyone else gets `claimed: false` and must stand
 * down, which is what keeps one confirmed payment from producing two builds
 * across the return handler, a webhook reconcile, a reload and a second tab.
 *
 * Never throws for the ordinary "nothing to claim" case — that is a null return,
 * not an error, because it is the answer on every normal page load.
 */
export async function claimPreparedBuild(
  projectId: string,
): Promise<BuildAttempt | null> {
  const data = (await apiFetch(`/api/projects/${projectId}/build-attempt`, {
    method: "POST",
    body: JSON.stringify({ claim: true }),
  })) as { attempt: BuildAttemptRow | null; claimed?: boolean };
  if (!data.claimed || !data.attempt) return null;
  return fromBuildAttemptRow(data.attempt);
}

/** Latest build attempt for a project (or null). Read on the recovery path. */
export async function getBuildAttempt(
  projectId: string,
): Promise<BuildAttempt | null> {
  const data = (await apiFetch(`/api/projects/${projectId}/build-attempt`, {
    method: "GET",
  })) as { attempt: BuildAttemptRow | null };
  return data.attempt ? fromBuildAttemptRow(data.attempt) : null;
}

export async function migratePlan(
  projectId: string,
  files: GeneratedFile[],
  plan: ProjectPlan,
): Promise<{ dbSchema: TableDef[] }> {
  return apiFetch(`/api/ai/migrate-plan`, {
    method: "POST",
    body: JSON.stringify({ projectId, files, plan }),
  });
}

export async function uploadAttachments(
  projectId: string,
  files: File[],
): Promise<{ attachments: Attachment[] }> {
  // Read pre-resize sizes via the resize-client WeakMap. Sent as a
  // parallel JSON-encoded array so the upload route can attach an
  // `originalSize` to each returned Attachment without changing the
  // file-binary contract (FormData "files" stays one part per file).
  const { getImageStats } = await import("./images/resize-client");
  const originalSizes: (number | null)[] = files.map((f) => {
    const s = getImageStats(f);
    return s ? s.originalSize : null;
  });

  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  form.append("originalSizes", JSON.stringify(originalSizes));

  const res = await fetch(`/api/projects/${projectId}/attachments`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `upload failed (${res.status})`);
  }
  return res.json();
}

export async function listProjects(): Promise<
  {
    id: string;
    title: string;
    prompt: string;
    created_at: string;
    updated_at: string;
  }[]
> {
  return apiFetch("/api/projects");
}

/**
 * LEGACY version listing — grouped by `files.version`, which counts revisions
 * PER FILE PATH and so names no moment in time: one entry spans many saves and
 * one save spans many entries. Nothing in the app calls this any more (the
 * version history panel reads {@link getSnapshots}); it is kept only until the
 * routes go with it. Do not build on it — see the route's header for why the
 * entries cannot be made into points in time.
 *
 * Throws now, rather than resolving to an empty list, when the read fails: an
 * auth blip used to arrive here as "you have no history".
 */
export async function getVersions(projectId: string): Promise<{
  versions: { version: number; created_at: string; files: string[] }[];
}> {
  return apiFetch(`/api/projects/${projectId}/versions`);
}

/**
 * LEGACY per-version file read, kept for the chat "go back to my published app"
 * flow — the one place that still has a `files.version` (resolved by
 * lib/projects/published-version.ts) and no seq to use instead. Everything else
 * restores through {@link getSnapshot}, which returns the exact stored bytes of
 * a real point in time.
 *
 * The result is safe to save: the route drops paths whose newest row at or
 * below the horizon is a tombstone, so a deleted file can no longer come back
 * as an empty file (which, for a route segment, is a hard `next build`
 * failure). It is still a PER-PATH horizon, so how far each file rewinds is not
 * uniform across the set — the reason this should move to a seq.
 *
 * Throws on failure instead of resolving to an empty set, so a caller cannot
 * mistake "the read failed" for "that version was empty".
 */
export async function restoreVersion(
  projectId: string,
  version: number,
): Promise<{ version: number; files: GeneratedFile[] }> {
  return apiFetch(`/api/projects/${projectId}/versions/${version}`);
}

/**
 * Which saved version the live site was published from — the target of "go back
 * to my published app". `status` distinguishes "never published" and "live but
 * unattributable" from a real hit, so callers explain rather than guess.
 */
export async function getPublishedVersion(
  projectId: string,
): Promise<PublishedVersionResponse> {
  return apiFetch(`/api/projects/${projectId}/published-version`);
}

export async function listSupabaseProjects(): Promise<SupabaseProject[]> {
  return apiFetch("/api/supabase-projects");
}

export async function connectSupabaseProject(
  supabaseRef: string,
  pandaProjectId: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/supabase-projects/${supabaseRef}/connect`, {
    method: "POST",
    body: JSON.stringify({ pandaProjectId }),
  });
}

export async function setupDatabase(
  projectId: string,
  dbSchema: TableDef[],
): Promise<{ success: boolean; error?: string }> {
  return apiFetch(`/api/projects/${projectId}/db-setup`, {
    method: "POST",
    body: JSON.stringify({ dbSchema }),
  });
}

export async function deleteProject(id: string) {
  return apiFetch(`/api/projects/${id}`, {
    method: "DELETE",
  });
}

export async function forkProject(id: string): Promise<{ id: string }> {
  return apiFetch(`/api/projects/${id}/fork`, {
    method: "POST",
  });
}

// --- Stripe Connect helpers ---

export async function getStripeStatus(
  projectId: string
): Promise<{ connected: boolean; connection?: StripeConnection }> {
  return apiFetch(`/api/stripe/connect/${projectId}`);
}

/**
 * Create the project's Express account. `country` is required and
 * permanent — Stripe fixes a connected account's country at creation and
 * never lets it change, so the caller must have collected it from the
 * user rather than letting Stripe fall back to the platform's country.
 *
 * `recreate` replaces an existing wrong-country account that hasn't taken
 * any payments yet (the only way to correct the country at all).
 */
export async function connectStripeExpress(
  projectId: string,
  country: string,
  recreate = false
): Promise<{
  accountId: string;
  country: string | null;
  replacedAccountId?: string;
  onboardingUrl: string;
}> {
  return apiFetch("/api/stripe/connect/express", {
    method: "POST",
    body: JSON.stringify({ projectId, country, recreate }),
  });
}

export async function getStripeConnectCountries(): Promise<{
  countries: string[];
  source: "stripe" | "fallback";
}> {
  return apiFetch("/api/stripe/connect/countries");
}

export async function refreshStripeLink(
  projectId: string
): Promise<{ onboardingUrl: string }> {
  return apiFetch("/api/stripe/connect/refresh", {
    method: "POST",
    body: JSON.stringify({ projectId }),
  });
}

export interface StripeDisconnectResult {
  success: boolean;
  /** True when there was nothing to disconnect — treated as success. */
  alreadyDisconnected?: boolean;
  /** What the platform did with the connected account itself. */
  outcome?: StripeReleaseOutcome;
  /** True when the account still exists on Stripe (nothing was deleted). */
  accountRetained?: boolean;
  accountType?: string;
}

/**
 * Disconnect the project's Stripe account. `intent` and `surface` are passed
 * through for the server-side audit/analytics record — "I'm removing payments"
 * and "I picked the wrong account" are different stories in the funnel.
 */
export async function disconnectStripe(
  projectId: string,
  opts?: { intent?: StripeDisconnectIntent; surface?: string }
): Promise<StripeDisconnectResult> {
  const query = new URLSearchParams();
  if (opts?.intent) query.set("intent", opts.intent);
  if (opts?.surface) query.set("surface", opts.surface);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return apiFetch(`/api/stripe/connect/${projectId}${suffix}`, {
    method: "DELETE",
  });
}

export async function updateStripeLinkToApp(
  projectId: string,
  linkedToApp: boolean
): Promise<{ success: boolean; linkedToApp: boolean }> {
  return apiFetch(`/api/stripe/connect/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify({ linkedToApp }),
  });
}

// --- GitHub + Deploy helpers ---

// Below this serialized-body size we send files inline; above it we skip the
// inline attempt (which would 413 at the platform request-size limit — e.g.
// Vercel's ~4.5MB serverless cap) and tell the route to publish from the files
// already persisted in the DB. Kept well under the platform ceiling to leave
// headroom for headers and the JSON envelope.
const GITHUB_PUSH_INLINE_LIMIT = 3_500_000;

export async function pushToGitHub(
  projectId: string,
  files: GeneratedFile[],
  message?: string,
  /** Publish context for the failure alert. Optional so the badge-toggle and
   *  save call sites keep their three-argument form. */
  opts?: { lastPrompt?: string | null; trigger?: string },
): Promise<{ status: string; commit_sha?: string }> {
  const url = `/api/projects/${projectId}/github-push`;

  // Forward the PostHog session id so a push-failure Slack alert can link
  // straight to this session's replay — the same best-effort lookup
  // deployProject does below. Never blocks the push.
  let sessionId = "";
  try {
    const posthog = (await import("posthog-js")).default;
    sessionId = posthog?.get_session_id?.() ?? "";
  } catch {
    // PostHog not ready — fall through with no session header.
  }
  const headers = sessionId ? { "x-posthog-session-id": sessionId } : undefined;
  // Bounded here as well as server-side: no reason to ship more than the alert
  // will show.
  const context = {
    lastPrompt: opts?.lastPrompt?.slice(0, 300) ?? null,
    trigger: opts?.trigger,
  };

  // Large generated apps can't ship every file blob in one request body. When
  // the payload is big, skip the doomed inline POST and have the route rebuild
  // the file set from the DB — persistFiles()/handleDeploy() write the current
  // files there before this call, so the stored set matches what we'd send.
  const inlineBody = JSON.stringify({ files, message, ...context });
  if (inlineBody.length > GITHUB_PUSH_INLINE_LIMIT) {
    console.warn(
      `[github-push] payload ${inlineBody.length}B over ${GITHUB_PUSH_INLINE_LIMIT}B (${files.length} file(s)) — publishing from stored files`,
    );
    return apiFetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ message, useStoredFiles: true, ...context }),
    });
  }

  try {
    return await apiFetch(url, { method: "POST", headers, body: inlineBody });
  } catch (err) {
    // A 413 slipped past the size check (a lower proxy limit than assumed).
    // Retry once from stored files instead of failing the push. Skip the
    // per-file cap failure (`files_too_large`) — those same files are in the
    // DB too, so a stored-files retry would just fail again.
    if (
      err instanceof ApiError &&
      err.status === 413 &&
      err.code !== "files_too_large"
    ) {
      console.warn(
        `[github-push] inline push 413 (${inlineBody.length}B, ${files.length} file(s)) — retrying from stored files`,
      );
      // Record the payload-size failure (dynamic import keeps posthog-js out of
      // this module's static graph, matching the session-id lookup above).
      void import("./analytics").then((m) =>
        m.trackPayloadTooLarge({
          operation: "github_push",
          projectId,
          fileCount: files.length,
          payloadBytes: inlineBody.length,
        }),
      );
      return apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ message, useStoredFiles: true, ...context }),
      });
    }
    throw err;
  }
}

/**
 * Why a deploy was triggered. "auto" = auto-publish after a successful build;
 * "manual" = user clicked Publish (dialog or share); "badge" = badge-setting
 * re-deploy; "mobile_preview" = automatic deploy to render the preview on a
 * WebContainer-unsupported browser; "auto_update" = an ALREADY-PUBLISHED app
 * carrying an ordinary edit to its live site by itself (lib/publish/live-update.ts).
 * Lets deploy telemetry separate genuine publishes from automatic deploys.
 *
 * "auto_update" is deliberately its own label rather than a reuse of "auto":
 * the two answer different questions ("did we publish for a user who never
 * had", vs "did a live site stay current"), the server keys the publish
 * counter and the reminder-email attribution off "manual" only, and the client
 * runs a leaner failure policy for this one — no AI auto-fix, one retry, then
 * the user takes over.
 */
export type DeployTrigger =
  | "auto"
  | "auto_update"
  | "manual"
  | "badge"
  | "mobile_preview";

export async function deployProject(
  projectId: string,
  trigger: DeployTrigger = "manual"
): Promise<{
  url?: string;
  deployStatus?: string;
  error?: string;
  deploymentId?: string;
  buildLogs?: string | null;
  errorMessage?: string | null;
  /** First actionable error lifted out of the build log, for the failure
   *  alert + UI instead of a generic "build failed" string (PANDA-1286). */
  errorSummary?: string | null;
  errorCode?: string | null;
  errorStep?: string | null;
  lastDeployedAt?: string | null;
  /** Per-project count of genuine user publishes (manual trigger only).
   *  Drives the post-publish Grow modal cadence. Present on a "ready" response. */
  publishCount?: number;
  /** Set on a pre-deploy artifact-validation 422 (the deploy source held the
   *  starter template instead of the generated app). "artifact_out_of_sync" =
   *  re-push and retry; "no_generated_app" = nothing generated yet. Distinct
   *  from a build failure — the client must NOT route these to the AI auto-fix. */
  code?: string;
  /** Whether the server captured anything a repair turn could actually read.
   *
   *  The client gates its repair offer on this rather than re-deriving it from
   *  `buildLogs`, so there is exactly one answer to "is there evidence here"
   *  and the two sides cannot drift. False means: do not ask a model to guess. */
  logAvailable?: boolean;
  /** How the build-log lookup ended — "captured" | "empty" | "unreachable" |
   *  "skipped" | "http_<status>". The reason behind `logAvailable: false`, so a
   *  recurrence is diagnosable without opening a session replay. */
  logLookup?: string;
  reason?: string;
  deployRequestId?: string;
}> {
  // Forward the PostHog session id so a deploy-failure Slack alert can link
  // straight to this session's replay. Best-effort: never blocks the deploy.
  let sessionId = "";
  try {
    const posthog = (await import("posthog-js")).default;
    sessionId = posthog?.get_session_id?.() ?? "";
  } catch {
    // PostHog not ready — fall through with no session header.
  }
  const res = await fetch(`/api/projects/${projectId}/deploy`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(sessionId ? { "x-posthog-session-id": sessionId } : {}),
    },
    body: JSON.stringify({ trigger }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 422) {
    // ApiError, not a bare Error: the body carries `code` (which the publish UI
    // reads to pick its recovery action) and `alerted` (which tells the
    // client-side failure beacon this incident already reached #failures from
    // the server). A plain Error dropped both, which is why every non-422
    // deploy failure used to reach PostHog as `failure_code: generic`.
    throw new ApiError(
      data.error || `Deploy failed (${res.status})`,
      res.status,
      data.code,
      data,
    );
  }
  return data;
}

export async function getDeployStatus(
  projectId: string
): Promise<{
  deploy_status: string | null;
  vercel_url: string | null;
  github_repo: string | null;
  last_deployed_at?: string | null;
  updated_at?: string | null;
  /** Per-project count of genuine user publishes (manual trigger only).
   *  Set once the build finalizes to "ready"; drives the Grow modal cadence. */
  publish_count?: number | null;
}> {
  return apiFetch(`/api/projects/${projectId}/deploy`);
}

/* ── Custom domains ──────────────────────────────────────────── */

/**
 * `verificationState` is the field to render from — it distinguishes the four
 * ways a domain can be "not live yet", which `verified` alone flattened into a
 * single misleading boolean. It is optional so a response cached from before
 * the server started sending it can't crash the panel; the UI falls back to
 * deriving a state from `verified`.
 *
 * `verified` now means "genuinely serving this app" (ownership settled AND DNS
 * resolving here), not Vercel's ownership bit. See lib/domains/verify-domain.ts.
 */
export type DomainVerificationState =
  | "active"
  | "pending_dns"
  | "pending_ownership"
  | "misdirected"
  | "unknown";

export interface CustomDomain {
  id: string;
  domain: string;
  status: "pending" | "active" | "error";
  verified: boolean;
  verificationState?: DomainVerificationState;
  /** Where the domain currently resolves, when that's somewhere other than us. */
  misdirectedTarget?: string | null;
  /** Last time we got a real answer out of Vercel. */
  lastCheckedAt?: string | null;
  verifiedAt: string | null;
  createdAt: string;
  verification: { type: string; domain: string; value: string }[] | null;
}

export async function getCustomDomain(
  projectId: string
): Promise<{ domain: CustomDomain | null }> {
  return apiFetch(`/api/projects/${projectId}/domains`);
}

export async function addCustomDomain(
  projectId: string,
  domain: string
): Promise<{ domain: CustomDomain }> {
  return apiFetch(`/api/projects/${projectId}/domains`, {
    method: "POST",
    body: JSON.stringify({ domain }),
  });
}

export async function removeCustomDomain(
  projectId: string
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/projects/${projectId}/domains`, {
    method: "DELETE",
  });
}

/** A buyable domain suggestion — priced with the 40% margin (PANDA-1013). */
export interface DomainSuggestion {
  domain: string;
  tld: string;
  /** What the user pays in USD (registrar price + 40% margin). */
  priceUsd: number;
  /** That charge in build credits. */
  credits: number;
}

export async function getDomainSuggestions(
  projectId: string
): Promise<{ suggestions: DomainSuggestion[]; code?: string }> {
  return apiFetch(`/api/projects/${projectId}/domains/suggestions`);
}

export async function buyDomain(
  projectId: string,
  domain: string
): Promise<{ ok: boolean; creditsSpent: number; domain: CustomDomain }> {
  return apiFetch(`/api/projects/${projectId}/domains/purchase`, {
    method: "POST",
    body: JSON.stringify({ domain }),
  });
}
